export const MIN_CLOCK_SPEED = 1;
export const MAX_CLOCK_SPEED = 100;
export const DEV_TIME_HEADER = 'X-6am-Dev-Now';

export type ClockState = Readonly<{
  enabled: boolean;
  realAnchorMs: number;
  simulatedAnchorMs: number;
  speed: number;
}>;

export function systemNowMs(): number {
  return Date.now();
}

export function systemNow(): Date {
  return new Date(systemNowMs());
}

export function clampClockSpeed(speed: number): number {
  if (!Number.isFinite(speed)) return MIN_CLOCK_SPEED;
  return Math.min(MAX_CLOCK_SPEED, Math.max(MIN_CLOCK_SPEED, Math.round(speed)));
}

export function createSystemClockState(realNowMs = systemNowMs()): ClockState {
  return {
    enabled: false,
    realAnchorMs: realNowMs,
    simulatedAnchorMs: realNowMs,
    speed: MIN_CLOCK_SPEED,
  };
}

export function clockNowMs(state: ClockState, realNowMs = systemNowMs()): number {
  if (!state.enabled) return realNowMs;
  return state.simulatedAnchorMs + (realNowMs - state.realAnchorMs) * state.speed;
}

export function clockNow(state: ClockState, realNowMs = systemNowMs()): Date {
  return new Date(clockNowMs(state, realNowMs));
}

export function setClockTime(
  state: ClockState,
  simulatedNow: Date | number,
  realNowMs = systemNowMs()
): ClockState {
  const simulatedAnchorMs = simulatedNow instanceof Date ? simulatedNow.getTime() : simulatedNow;
  if (!Number.isFinite(simulatedAnchorMs)) return state;
  return {
    enabled: true,
    realAnchorMs: realNowMs,
    simulatedAnchorMs,
    speed: clampClockSpeed(state.speed),
  };
}

export function jumpClockBy(
  state: ClockState,
  durationMs: number,
  realNowMs = systemNowMs()
): ClockState {
  if (!Number.isFinite(durationMs)) return state;
  return setClockTime(state, clockNowMs(state, realNowMs) + durationMs, realNowMs);
}

export function setClockSpeed(
  state: ClockState,
  speed: number,
  realNowMs = systemNowMs()
): ClockState {
  const currentMs = clockNowMs(state, realNowMs);
  const nextSpeed = clampClockSpeed(speed);
  return {
    enabled: state.enabled || nextSpeed !== MIN_CLOCK_SPEED,
    realAnchorMs: realNowMs,
    simulatedAnchorMs: currentMs,
    speed: nextSpeed,
  };
}

export function isClockState(value: unknown): value is ClockState {
  if (!value || typeof value !== 'object') return false;
  const candidate = value as Partial<ClockState>;
  return (
    typeof candidate.enabled === 'boolean' &&
    typeof candidate.realAnchorMs === 'number' &&
    Number.isFinite(candidate.realAnchorMs) &&
    typeof candidate.simulatedAnchorMs === 'number' &&
    Number.isFinite(candidate.simulatedAnchorMs) &&
    typeof candidate.speed === 'number' &&
    candidate.speed >= MIN_CLOCK_SPEED &&
    candidate.speed <= MAX_CLOCK_SPEED
  );
}
